import { useState } from 'react'
import { useLocation } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { Search, Users } from 'lucide-react'
import { PageHeader } from '@/shared/ui/PageHeader'
import { Card } from '@/shared/ui/Card'
import { Spinner } from '@/shared/ui/Spinner'
import { dashboardService } from '@/api/services/dashboardService'
import { useCities } from '@/api/hooks'
import { BarRankingChart, PALETTE } from './charts'

const MONTHS = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec']

export function TrainerSessionsPage() {
  const location = useLocation()
  const base = location.pathname.startsWith('/school') ? '/school/overview' : '/dashboard'

  const [cityId, setCityId] = useState<number | ''>('')
  const [search, setSearch] = useState('')
  const [monthFilter, setMonthFilter] = useState('all')

  const { data: citiesData } = useCities({ limit: 1000 })
  const cities: any[] = citiesData?.data ?? []

  const insightsQ = useQuery({
    queryKey: ['dashboard', 'trainer-insights', cityId],
    queryFn: () => dashboardService.trainerInsights({ city_id: cityId || undefined }),
    staleTime: 30_000,
  })
  const trainerInsights: any = (insightsQ.data as any)?.data ?? insightsQ.data ?? {}
  const sessionsPerTrainerRaw: any[] = trainerInsights?.sessionsPerTrainer ?? []

  const sessionsAvailableMonths = Array.from(
    new Set(sessionsPerTrainerRaw.flatMap((t: any) => (t.sessions ?? []).map((s: any) => Number(s.month))))
  ).sort((a, b) => a - b)

  const trainerSessionsRanked = sessionsPerTrainerRaw
    .map((t: any) => {
      const sessions = t.sessions ?? []
      const totalSessions = monthFilter === 'all'
        ? sessions.reduce((s: number, x: any) => s + (Number(x.session_count) || 0), 0)
        : Number(sessions.find((x: any) => Number(x.month) === Number(monthFilter))?.session_count) || 0
      return { trainer_id: t.trainer_id, trainer_name: t.trainer_name, trainer_email: t.trainer_email, totalSessions }
    })
    .filter((t: any) => t.totalSessions > 0)
    .filter((t: any) => !search.trim() || String(t.trainer_name ?? '').toLowerCase().includes(search.trim().toLowerCase()))
    .sort((a: any, b: any) => b.totalSessions - a.totalSessions)

  // Chart reads top to bottom in series order; reverse so #1 renders at the top.
  const chartRows = [...trainerSessionsRanked].reverse()

  return (
    <div className="space-y-5">
      <PageHeader
        title="Trainer Sessions"
        subtitle="All trainers ranked by total sessions"
        breadcrumbs={[{ label: 'Dashboard', to: base }, { label: 'Trainer Sessions' }]}
      />

      {/* ── Filters ── */}
      <Card>
        <div className="flex items-center gap-2 flex-wrap">
          <div className="relative flex-1 min-w-48">
            <Search size={14} className="absolute start-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Search trainer by name…"
              className="w-full ps-9 pe-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-400"
            />
          </div>
          <select
            value={cityId}
            onChange={e => setCityId(e.target.value ? Number(e.target.value) : '')}
            className="text-sm border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-brand-400 min-w-36 bg-white"
          >
            <option value="">All Cities</option>
            {cities.map((c: any) => (
              <option key={c.id} value={c.id}>
                {c.city_translations?.map((t: any) => t.name).join(' - ') || c.name}
              </option>
            ))}
          </select>
          {sessionsAvailableMonths.length > 0 && (
            <select
              value={monthFilter}
              onChange={e => setMonthFilter(e.target.value)}
              className="text-sm border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-brand-400 bg-white"
            >
              <option value="all">All months</option>
              {sessionsAvailableMonths.map(m => <option key={m} value={m}>{MONTHS[m - 1] ?? `Month ${m}`}</option>)}
            </select>
          )}
          <span className="inline-flex items-center gap-1.5 rounded-full bg-gray-100 px-3 py-1.5 text-xs font-semibold text-gray-600">
            <Users size={12} /> {trainerSessionsRanked.length} trainers
          </span>
        </div>
      </Card>

      {/* ── Graph ── */}
      <Card>
        {insightsQ.isLoading ? (
          <div className="flex justify-center py-20"><Spinner /></div>
        ) : trainerSessionsRanked.length === 0 ? (
          <p className="text-sm text-gray-400 text-center py-20">No session data found.</p>
        ) : (
          <div className="max-h-[70vh] overflow-y-auto">
            <BarRankingChart
              categories={chartRows.map((tr: any) => tr.trainer_name ?? '—')}
              data={chartRows.map((tr: any) => tr.totalSessions)}
              name="Sessions"
              unit="sessions"
              color={PALETTE.primary.main}
            />
          </div>
        )}
      </Card>
    </div>
  )
}
